import React, { useEffect, useState, useCallback, useRef } from 'react';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  Navigation, RefreshCw, Search, Phone, Mail, MapPin, AlertTriangle,
  Clock, Car, CheckCircle, X, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const M_PER_MILE = 1609.34;

const STATUS_META = {
  preferred:     { label: 'Good', color: '#4ade80', avoid: false },
  sheriff:       { label: "Sheriff's Office", color: '#e0b341', avoid: false },
  international: { label: 'International', color: '#ec4899', avoid: false },
  avoid:         { label: 'Do not send work', color: '#f87171', avoid: true },
  retired:       { label: 'Retired', color: '#c084fc', avoid: true },
  other:         { label: 'Other', color: '#9ca3af', avoid: false },
};

// Dark map theme to match the portal's ink palette. Place/city labels are kept
// bright (with a dark halo) so towns are legible as you zoom, while road/POI
// clutter stays hidden.
const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#161616' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#b9c0c8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }, { weight: 2 }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2a2a2a' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#3a3a3a' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#e8e8e8' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#2f2f2f' }] },
  { featureType: 'administrative.province', elementType: 'labels.text.fill', stylers: [{ color: '#d8d8d8' }] },
  // Hide Google's own town/neighborhood labels — we draw our own dataset labels
  // instead, so this prevents duplicate (doubled) city names on the map.
  { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#242424' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2e2e2e' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d0d0d' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5a7a8a' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#141414' }] },
];

// Progressive town/city labels, sourced from a local US Census/GeoNames places
// dataset (`/us_places.json`, ~23k US towns + major world cities). Google's
// classic styling can't force label DENSITY and its own steps are coarse, so we
// draw our own labels and REVEAL MORE as the user zooms in. Each place carries a
// population-derived tier `t` (1 = biggest metro ... 7 = small town); a tier's
// labels switch on once the map is zoomed to its `minZoom` and stay on deeper,
// so density always grows (never shrinks) on zoom-in. Labels are rendered
// viewport-first and capped, so only a couple hundred draw at once regardless of
// dataset size.
const PLACES_URL = '/us_places.json';
const LABEL_CAP = 260;              // max labels drawn at once
const TIER_MIN_ZOOM = { 1: 4, 2: 6, 3: 7, 4: 8, 5: 9, 6: 10, 7: 11 };

// Per-tier label styling: bigger/brighter for major metros, smaller/dimmer for
// small towns — mirrors a natural map hierarchy now that Google's own town
// labels are hidden (we're the only label source).
const LABEL_STYLE = {
  1: { color: '#e6ebf1', fontSize: '13px', fontWeight: '600' },
  2: { color: '#d3dae2', fontSize: '12px', fontWeight: '500' },
  3: { color: '#c2cad3', fontSize: '11px', fontWeight: '500' },
  4: { color: '#aab3bd', fontSize: '10px', fontWeight: '400' },
  5: { color: '#98a2ad', fontSize: '10px', fontWeight: '400' },
  6: { color: '#8b95a0', fontSize: '9px',  fontWeight: '400' },
  7: { color: '#7e8894', fontSize: '9px',  fontWeight: '400' },
};

// Highest tier number visible at a given zoom (inverse of TIER_MIN_ZOOM).
const maxTierForZoom = (z) => {
  let max = 0;
  for (const [tier, mz] of Object.entries(TIER_MIN_ZOOM)) {
    if (z >= mz && Number(tier) > max) max = Number(tier);
  }
  return max;
};

const TRANSPARENT_ICON = {
  url: 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221%22 height=%221%22%3E%3C/svg%3E',
};

const waitForGoogle = () => new Promise((resolve) => {
  if (window.google?.maps?.DistanceMatrixService) return resolve(true);
  const t = setInterval(() => {
    if (window.google?.maps?.DistanceMatrixService) { clearInterval(t); resolve(true); }
  }, 150);
  setTimeout(() => { clearInterval(t); resolve(!!window.google?.maps); }, 12000);
});

const AdminV2ServerFinder = () => {
  const { token } = useAuth();
  const authFetch = useCallback((path, opts = {}) => fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  }), [token]);

  const [servers, setServers] = useState([]);
  const [meta, setMeta] = useState({ count: 0, by_category: {}, synced_at: null });
  const [syncing, setSyncing] = useState(false);
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(30);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);   // { origin, matches:[], nearestOverall }
  const [error, setError] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [gapLogged, setGapLogged] = useState(false);
  const addrRef = useRef(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markersRef = useRef([]);
  const originMarkerRef = useRef(null);
  const circleRef = useRef(null);
  const cityMarkersRef = useRef([]);   // pooled reusable label markers
  const placesRef = useRef([]);        // loaded places dataset (sorted by tier)
  const [mapReady, setMapReady] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);

  const loadServers = useCallback(async () => {
    const r = await authFetch('/api/admin/servers');
    if (r.ok) { const d = await r.json(); setServers(d.servers || []); setMeta(d); }
  }, [authFetch]);

  const loadGaps = useCallback(async () => {
    const r = await authFetch('/api/admin/servers/gaps');
    if (r.ok) setGaps((await r.json()).gaps || []);
  }, [authFetch]);

  useEffect(() => { loadServers(); loadGaps(); }, [loadServers, loadGaps]);

  // Initialize the map once Google Maps is ready.
  useEffect(() => {
    let cancelled = false;
    waitForGoogle().then((ok) => {
      if (cancelled || !ok || !mapRef.current || mapObj.current) return;
      mapObj.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 39.5, lng: -98.35 }, zoom: 4,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
        backgroundColor: '#0a0a0a',
        styles: DARK_MAP_STYLE,
      });
      setMapReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Progressive town/city labels driven by the local places dataset. Loads once,
  // then on every map 'idle' (after pan/zoom) redraws only the labels that fall
  // inside the current viewport whose tier is unlocked at the current zoom,
  // capped at LABEL_CAP and prioritised biggest-first. Markers are pooled/reused
  // so we never create thousands. More towns surface the more you zoom in.
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    let cancelled = false;
    const map = mapObj.current;
    const google = window.google;

    const ensureMarker = (i) => {
      let m = cityMarkersRef.current[i];
      if (!m) {
        m = new google.maps.Marker({
          map,
          clickable: false,
          zIndex: 1,
          icon: { ...TRANSPARENT_ICON, scaledSize: new google.maps.Size(1, 1) },
        });
        cityMarkersRef.current[i] = m;
      }
      return m;
    };

    const render = () => {
      const places = placesRef.current;
      if (!places.length) return;
      const z = map.getZoom() || 0;
      const maxTier = maxTierForZoom(z);
      const bounds = map.getBounds();
      const pool = cityMarkersRef.current;
      if (!maxTier || !bounds) {
        pool.forEach(m => m.setVisible(false));
        return;
      }
      // places are pre-sorted by tier asc, so we can stop once tier exceeds max.
      const sel = [];
      for (const p of places) {
        if (p.t > maxTier) break;
        if (bounds.contains({ lat: p.y, lng: p.x })) {
          sel.push(p);
          if (sel.length >= LABEL_CAP) break;
        }
      }
      sel.forEach((p, i) => {
        const m = ensureMarker(i);
        m.setPosition({ lat: p.y, lng: p.x });
        m.setLabel({ text: p.n, ...(LABEL_STYLE[p.t] || LABEL_STYLE[5]) });
        m.setVisible(true);
      });
      for (let i = sel.length; i < pool.length; i++) pool[i].setVisible(false);
    };

    const listener = map.addListener('idle', render);

    fetch(PLACES_URL)
      .then(r => (r.ok ? r.json() : []))
      .then(data => {
        if (cancelled) return;
        placesRef.current = Array.isArray(data) ? data : [];
        render();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (listener) listener.remove();
      cityMarkersRef.current.forEach(m => m.setMap(null));
      cityMarkersRef.current = [];
    };
  }, [mapReady]);

  // (Re)draw a colored marker per server whenever the list changes.
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    // Draw dots as SVG image icons (reliable across all Maps versions) rather
    // than vector SymbolPath, which wasn't rendering in some browsers.
    const dotIcon = (color) => ({
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="9" cy="9" r="6.5" fill="${color}" stroke="#0a0a0a" stroke-width="1.5"/></svg>`
      ),
      scaledSize: new window.google.maps.Size(18, 18),
      anchor: new window.google.maps.Point(9, 9),
    });
    servers.forEach((s) => {
      if (typeof s.lat !== 'number' || typeof s.lng !== 'number') return;
      const meta = STATUS_META[s.status] || STATUS_META.other;
      const marker = new window.google.maps.Marker({
        position: { lat: s.lat, lng: s.lng },
        map: mapObj.current,
        title: s.name,
        zIndex: 10,
        icon: dotIcon(meta.color),
      });
      marker.addListener('click', () => {
        mapObj.current.panTo({ lat: s.lat, lng: s.lng });
        setSelectedServer(s);
      });
      markersRef.current.push(marker);
    });
  }, [servers, mapReady]);

  // Drop the search origin + radius circle and frame the matches on the map.
  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    if (originMarkerRef.current) { originMarkerRef.current.setMap(null); originMarkerRef.current = null; }
    if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }
    if (!results?.origin) return;
    const { lat, lng } = results.origin;
    originMarkerRef.current = new window.google.maps.Marker({
      position: { lat, lng }, map: mapObj.current, zIndex: 999, title: 'Service address',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"><circle cx="13" cy="13" r="9" fill="#2563eb" stroke="#ffffff" stroke-width="3"/></svg>'
        ),
        scaledSize: new window.google.maps.Size(26, 26),
        anchor: new window.google.maps.Point(13, 13),
      },
    });
    circleRef.current = new window.google.maps.Circle({
      map: mapObj.current, center: { lat, lng }, radius: (results.radius || 30) * M_PER_MILE,
      strokeColor: '#2563eb', strokeOpacity: 0.55, strokeWeight: 1, fillColor: '#2563eb', fillOpacity: 0.06,
    });
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat, lng });
    (results.matches || []).forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    if (results.matches && results.matches.length) {
      mapObj.current.fitBounds(bounds, 60);
    } else {
      mapObj.current.setCenter({ lat, lng });
      mapObj.current.setZoom(9);
    }
  }, [results, mapReady]);

  // Attach Places Autocomplete to the address box (same key/libs as the rest of the app)
  useEffect(() => {
    let ac;
    waitForGoogle().then(() => {
      if (addrRef.current && window.google?.maps?.places) {
        ac = new window.google.maps.places.Autocomplete(addrRef.current, {
          componentRestrictions: { country: 'us' }, fields: ['formatted_address', 'geometry'],
        });
        ac.addListener('place_changed', () => {
          const p = ac.getPlace();
          if (p?.formatted_address) setAddress(p.formatted_address);
        });
      }
    });
  }, []);

  const syncMap = async () => {
    setSyncing(true); setError(null);
    try {
      const r = await authFetch('/api/admin/servers/sync', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Sync failed');
      await loadServers();
    } catch (e) { setError(e.message); }
    finally { setSyncing(false); }
  };

  const haversineMi = (aLat, aLng, bLat, bLng) => {
    const g = window.google?.maps?.geometry?.spherical;
    if (g) {
      const m = g.computeDistanceBetween(
        new window.google.maps.LatLng(aLat, aLng),
        new window.google.maps.LatLng(bLat, bLng),
      );
      return m / M_PER_MILE;
    }
    const R = 3958.8, dLat = (bLat - aLat) * Math.PI / 180, dLng = (bLng - aLng) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  const drivingMatrix = (origin, dests) => new Promise((resolve) => {
    const svc = new window.google.maps.DistanceMatrixService();
    const out = new Array(dests.length).fill(null);
    let done = 0;
    const chunks = [];
    for (let i = 0; i < dests.length; i += 25) chunks.push({ start: i, items: dests.slice(i, i + 25) });
    if (!chunks.length) return resolve(out);
    chunks.forEach((chunk) => {
      svc.getDistanceMatrix({
        origins: [origin],
        destinations: chunk.items.map(d => new window.google.maps.LatLng(d.lat, d.lng)),
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      }, (res, status) => {
        if (status === 'OK' && res?.rows?.[0]?.elements) {
          res.rows[0].elements.forEach((el, idx) => {
            if (el.status === 'OK') {
              out[chunk.start + idx] = { miles: el.distance.value / M_PER_MILE, driveText: el.duration.text, driveSecs: el.duration.value };
            }
          });
        }
        done += 1;
        if (done === chunks.length) resolve(out);
      });
    });
  });

  const runSearch = async () => {
    setError(null); setResults(null); setGapLogged(false);
    if (!address.trim()) { setError('Enter an address to search.'); return; }
    if (!servers.length) { setError('No servers loaded. Click "Re-sync from Google Map" first.'); return; }
    setSearching(true);
    try {
      const ready = await waitForGoogle();
      if (!ready) throw new Error('Google Maps is still loading — try again in a moment.');
      const geocoder = new window.google.maps.Geocoder();
      const geo = await new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (res, status) => {
          if (status === 'OK' && res[0]) resolve(res[0]);
          else reject(new Error('Could not find that address.'));
        });
      });
      const comps = geo.address_components || [];
      const countyComp = comps.find((c) => (c.types || []).includes('administrative_area_level_2'));
      const county = countyComp ? countyComp.long_name.replace(/\s+County$/i, '') : null;
      const origin = { lat: geo.geometry.location.lat(), lng: geo.geometry.location.lng(), formatted: geo.formatted_address, county };

      // Pre-filter by straight-line (a lower bound on driving distance): anything
      // farther than the radius as the crow flies can't be within it by road.
      const rad = parseFloat(radius) || 30;
      const candidates = servers
        .map(s => ({ ...s, crow: haversineMi(origin.lat, origin.lng, s.lat, s.lng) }))
        .filter(s => s.crow <= rad + 0.5)
        .sort((a, b) => a.crow - b.crow)
        .slice(0, 50); // safety cap on Distance Matrix volume

      let matches = [];
      let nearestOverall = null;
      if (candidates.length) {
        const drives = await drivingMatrix({ lat: origin.lat, lng: origin.lng }, candidates);
        const withDrive = candidates.map((c, i) => ({ ...c, ...(drives[i] || {}) }))
          .filter(c => c.miles != null)
          .sort((a, b) => a.driveSecs - b.driveSecs);
        nearestOverall = withDrive[0] || null;
        matches = withDrive.filter(c => c.miles <= rad);
      }
      setResults({ origin, matches, nearestOverall, radius: rad });

      if (!matches.length) {
        // auto-remember the gap so the owner can recruit here
        await authFetch('/api/admin/servers/gaps', {
          method: 'POST',
          body: JSON.stringify({
            address: origin.formatted, lat: origin.lat, lng: origin.lng, radius_miles: rad,
            nearest_name: nearestOverall?.name || null,
            nearest_miles: nearestOverall ? Math.round(nearestOverall.miles * 10) / 10 : null,
          }),
        });
        setGapLogged(true);
        loadGaps();
      }
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  };

  const removeGap = async (id) => {
    await authFetch(`/api/admin/servers/gaps/${id}`, { method: 'DELETE' });
    loadGaps();
  };

  return (
    <PortalV2Layout
      title="Find Nearest Server"
      subtitle="Type an address and get your closest contract servers by drive time"
      action={
        <button onClick={syncMap} disabled={syncing} className="pv2-btn pv2-btn-ghost" data-testid="pv2-server-sync">
          <RefreshCw size={14} strokeWidth={1.5} className={syncing ? 'pv2-skeleton' : ''} />
          {syncing ? 'Syncing…' : 'Re-sync from Google Map'}
        </button>
      }
    >
      <div style={{ marginBottom: 10 }} className="pv2-mono-sm">
        {meta.count} servers on file
        {meta.synced_at ? ` · last synced ${new Date(meta.synced_at).toLocaleString()}` : ' · not synced yet'}
      </div>

      {/* Color legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }} data-testid="pv2-server-legend">
        {[
          ['Good', '#4ade80'],
          ['Do not send work', '#f87171'],
          ['International', '#ec4899'],
          ["Sheriff's Office", '#e0b341'],
          ['Retired', '#c084fc'],
        ].map(([label, color]) => (
          <span key={label} className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
            <span style={{ color }}>{label}</span>
          </span>
        ))}
      </div>

      {/* Search box */}
      <div style={{ background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)', padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 340px' }}>
            <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Service Address</label>
            <input
              ref={addrRef}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="123 Main St, Lubbock, TX"
              className="pv2-input"
              style={{ width: '100%' }}
              data-testid="pv2-server-address"
            />
          </div>
          <div style={{ width: 120 }}>
            <label className="pv2-mono-sm" style={{ display: 'block', marginBottom: 6 }}>Radius (mi)</label>
            <input type="number" min={1} max={200} value={radius}
              onChange={(e) => setRadius(e.target.value)} className="pv2-input" style={{ width: '100%' }}
              data-testid="pv2-server-radius" />
          </div>
          <button onClick={runSearch} disabled={searching} className="pv2-btn pv2-btn-primary" data-testid="pv2-server-search">
            <Search size={14} /> {searching ? 'Searching…' : 'Find Servers'}
          </button>
        </div>
        {error && (
          <div className="pv2-alert pv2-alert-red" style={{ marginTop: 14 }} data-testid="pv2-server-error">
            <AlertTriangle size={14} /><span>{error}</span>
          </div>
        )}
      </div>

      {/* Live map of all servers (color-coded) + the searched address */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <div
          ref={mapRef}
          data-testid="pv2-server-map"
          style={{ width: '100%', height: 460, border: '1px solid var(--pv2-ink-line)', background: 'var(--pv2-ink)' }}
        />
        {selectedServer && (() => {
          const meta = STATUS_META[selectedServer.status] || STATUS_META.other;
          const tel = selectedServer.phone ? selectedServer.phone.replace(/[^\d+]/g, '') : '';
          return (
            <div
              data-testid="pv2-server-detail-pane"
              style={{
                position: 'absolute', top: 14, right: 14, width: 268, zIndex: 5,
                background: 'rgba(10,10,10,0.96)', backdropFilter: 'blur(8px)',
                border: `1px solid ${meta.color}`, boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                padding: '16px 16px 14px',
              }}
            >
              <button
                onClick={() => setSelectedServer(null)}
                data-testid="pv2-server-detail-close"
                aria-label="Close"
                style={{ position: 'absolute', top: 8, right: 8, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line)', color: '#fff', cursor: 'pointer', borderRadius: 4 }}
              >
                <X size={15} strokeWidth={2} />
              </button>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
                <span className="pv2-mono-sm" style={{ color: meta.color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{meta.label}</span>
              </div>
              <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', paddingRight: 24, marginBottom: 12, lineHeight: 1.25 }}>
                {selectedServer.name}
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                {selectedServer.phone && (
                  <a href={`tel:${tel}`} data-testid="pv2-detail-phone"
                    style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#fff', textDecoration: 'none', fontSize: 14, padding: '8px 10px', background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line)' }}>
                    <Phone size={14} color={meta.color} /> {selectedServer.phone}
                  </a>
                )}
                {selectedServer.email && (
                  <a href={`mailto:${selectedServer.email}`} data-testid="pv2-detail-email"
                    style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#fff', textDecoration: 'none', fontSize: 13, padding: '8px 10px', background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line)', wordBreak: 'break-all' }}>
                    <Mail size={14} color={meta.color} /> {selectedServer.email}
                  </a>
                )}
                {!selectedServer.phone && !selectedServer.email && (
                  <div className="pv2-mono-sm" style={{ color: 'var(--pv2-text-subtle)' }}>No contact info on file.</div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
      {!mapReady && (
        <div className="pv2-mono-sm" style={{ marginTop: -16, marginBottom: 20, color: 'var(--pv2-text-subtle)' }}>Loading map…</div>
      )}

      {/* Results */}
      {results && (
        <div style={{ marginBottom: 28 }} data-testid="pv2-server-results">
          <div className="pv2-mono-sm" style={{ marginBottom: 12 }}>
            <MapPin size={12} style={{ display: 'inline', marginRight: 4 }} />
            {results.origin.formatted}
            {results.origin.county && (
              <span data-testid="pv2-finder-county" style={{ marginLeft: 8, color: 'var(--pv2-red-bright)', fontWeight: 700 }}>· {results.origin.county} County</span>
            )}
          </div>

          {results.matches.length > 0 ? (
            <>
              <div style={{ fontSize: 14, marginBottom: 12 }}>
                {results.matches.length} server{results.matches.length > 1 ? 's' : ''} within {results.radius} driving miles
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {results.matches.map((s) => <ServerCard key={s.id} s={s} />)}
              </div>
            </>
          ) : (
            <div className="pv2-alert pv2-alert-amber" data-testid="pv2-server-gap">
              <AlertTriangle size={16} />
              <div>
                <div style={{ fontWeight: 600 }}>No server within {results.radius} driving miles.</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {results.nearestOverall
                    ? <>Closest is <strong>{results.nearestOverall.name}</strong> at ~{results.nearestOverall.miles.toFixed(1)} mi ({results.nearestOverall.driveText}).</>
                    : 'No servers found nearby at all.'}
                  {gapLogged && <> This address was saved to <strong>Coverage Gaps</strong> below so you can recruit someone.</>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Coverage gaps */}
      <div style={{ borderTop: '1px solid var(--pv2-ink-line)', paddingTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Coverage Gaps</div>
        <div className="pv2-mono-sm" style={{ marginBottom: 12 }}>Addresses with no nearby server — recruit a server in these areas.</div>
        {gaps.length === 0 ? (
          <div className="pv2-mono-sm" style={{ color: 'var(--pv2-text-subtle)' }}>No gaps logged.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {gaps.map((g) => (
              <div key={g.id} data-testid="pv2-gap-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line-soft)' }}>
                <div>
                  <div style={{ fontSize: 13 }}>{g.address}</div>
                  <div className="pv2-mono-sm" style={{ marginTop: 2 }}>
                    within {g.radius_miles} mi
                    {g.nearest_name ? ` · nearest: ${g.nearest_name} (~${g.nearest_miles} mi)` : ''}
                    {g.created_at ? ` · ${new Date(g.created_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <button onClick={() => removeGap(g.id)} className="pv2-btn pv2-btn-ghost" style={{ padding: '6px 10px' }} data-testid="pv2-gap-resolve" title="Mark resolved / remove">
                  <CheckCircle size={13} /> Resolve
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </PortalV2Layout>
  );
};

const ServerCard = ({ s }) => {
  const meta = STATUS_META[s.status] || STATUS_META.other;
  return (
    <div
      data-testid="pv2-server-card"
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        padding: '14px 16px', background: 'var(--pv2-ink)',
        border: `1px solid ${meta.avoid ? 'rgba(248,113,113,0.4)' : 'var(--pv2-ink-line)'}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: meta.color }} title={meta.label} data-testid="pv2-server-name">{s.name}</span>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
          {s.phone && (
            <a href={`tel:${s.phone.replace(/[^\d+]/g, '')}`} className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--pv2-red-bright)' }} data-testid="pv2-server-phone">
              <Phone size={12} /> {s.phone}
            </a>
          )}
          {s.email && (
            <a href={`mailto:${s.email}`} className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--pv2-red-bright)' }} data-testid="pv2-server-email">
              <Mail size={12} /> {s.email}
            </a>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 20, fontFamily: 'Playfair Display, serif', fontWeight: 700 }}>{s.miles.toFixed(1)} mi</div>
        <div className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
          <Car size={11} /> {s.driveText}
        </div>
      </div>
    </div>
  );
};

export default AdminV2ServerFinder;
